import type { AxiosError } from "axios";
import { worktimeApi } from "@wkt/api/worktime";
import type {
  ActivityDTO,
  CheckIfAnyActivityConflictsResponseDTO,
  CurrentWorkdayServiceResponseDTO,
  FormDTO,
  FormSubmissionDTO,
  FullWorkdayDTO,
  FullWorkdayMonthlyStatsDTO,
  JiraAuthenticateRequestDTO,
  JiraImportRequestDTO,
  PersonalNoteDTO,
  SimpleDateDTO,
  SimpleDateRangeDTO,
  VacationDataDTO,
  WorkdayDTO,
  WorkdayMonthlyStatsDTO,
  WorkdayServiceResponseDTO,
  EmployeeCalendarDTO,
} from "./checkinDTO";

type SuccessCallback<T = unknown> = (data: T) => void;
type ErrorCallback = (error: AxiosError) => void;

export class CheckinAPI {
  getCurrentWorkday(
    workdayDate: SimpleDateDTO,
    onSuccess: SuccessCallback<CurrentWorkdayServiceResponseDTO>,
    onError: ErrorCallback,
  ): void {
    worktimeApi.genericPost(
      "user/currentWorkday",
      workdayDate,
      onSuccess,
      onError,
    );
  }

  workdayStart(
    workday: WorkdayDTO,
    onSuccess: SuccessCallback<WorkdayDTO>,
    onError: ErrorCallback,
  ): void {
    worktimeApi.genericPost("user/workdayStart", workday, onSuccess, onError);
  }

  workdayFinish(
    workday: WorkdayDTO,
    onSuccess: SuccessCallback<WorkdayServiceResponseDTO>,
    onError: ErrorCallback,
  ): void {
    worktimeApi.genericPost("user/workdayFinish", workday, onSuccess, onError);
  }

  activityStart(
    activity: ActivityDTO,
    onSuccess: SuccessCallback<WorkdayDTO>,
    onError: ErrorCallback,
  ): void {
    worktimeApi.genericPost("user/activityStart", activity, onSuccess, onError);
  }

  activityFinish(
    activity: ActivityDTO,
    onSuccess: SuccessCallback<WorkdayServiceResponseDTO>,
    onError: ErrorCallback,
  ): void {
    worktimeApi.genericPost(
      "user/activityFinish",
      activity,
      onSuccess,
      onError,
    );
  }

  createActivity(
    activity: ActivityDTO,
    onSuccess: SuccessCallback<WorkdayServiceResponseDTO>,
    onError: ErrorCallback,
  ): void {
    worktimeApi.genericPost(
      "user/createActivity",
      activity,
      onSuccess,
      onError,
    );
  }

  updateActivity(
    activity: ActivityDTO,
    onSuccess: SuccessCallback<WorkdayServiceResponseDTO>,
    onError: ErrorCallback,
  ): void {
    worktimeApi.genericPost(
      "user/updateActivity",
      activity,
      onSuccess,
      onError,
    );
  }

  deleteActivity(
    activityId: number,
    onSuccess: SuccessCallback<WorkdayDTO>,
    onError: ErrorCallback,
  ): void {
    worktimeApi.genericDelete(
      `user/deleteActivity/${activityId}`,
      onSuccess,
      onError,
    );
  }

  checkIfAnyActivityConflicts(
    workdayId: number,
    onSuccess: SuccessCallback<CheckIfAnyActivityConflictsResponseDTO>,
    onError: ErrorCallback,
  ): void {
    worktimeApi.genericGet(
      `user/anyActivityOverlaps/${workdayId}`,
      onSuccess,
      onError,
    );
  }

  getCalendar(
    dateRange: SimpleDateRangeDTO,
    onSuccess: SuccessCallback<EmployeeCalendarDTO[]>,
    onError: ErrorCallback,
  ): void {
    worktimeApi.genericPost("user/calendar", dateRange, onSuccess, onError);
  }

  getVacationData(
    onSuccess: SuccessCallback<VacationDataDTO>,
    onError: ErrorCallback,
  ): void {
    worktimeApi.genericGet("user/vacationData", onSuccess, onError);
  }

  setPersonalNote(
    note: PersonalNoteDTO,
    onSuccess: SuccessCallback<void>,
    onError: ErrorCallback,
  ): void {
    worktimeApi.genericPost("user/personalNote", note, onSuccess, onError);
  }

  getTeamStats(
    date: SimpleDateDTO,
    onSuccess: SuccessCallback<FullWorkdayDTO[]>,
    onError: ErrorCallback,
  ): void {
    worktimeApi.genericPost("user/teamStats", date, onSuccess, onError);
  }

  getWorkdayMonthlyStats(
    year: number,
    month: number,
    onSuccess: SuccessCallback<WorkdayMonthlyStatsDTO>,
    onError: ErrorCallback,
  ): void {
    worktimeApi.genericGet(
      `user/workdayMonthlyStats/${year}/${month}`,
      onSuccess,
      onError,
    );
  }

  getMonthDataContainer(
    onSuccess: SuccessCallback<FullWorkdayMonthlyStatsDTO[]>,
    onError: ErrorCallback,
  ): void {
    worktimeApi.genericGet("user/getMonthData", onSuccess, onError);
  }

  getMonthlyWorkdaysByEmployeeId(
    year: number,
    month: number,
    onSuccess: SuccessCallback<FullWorkdayDTO[]>,
    onError: ErrorCallback,
  ): void {
    worktimeApi.genericGet(
      `user/monthlyWorkdaysByEmployeeId/${year}/${month}`,
      onSuccess,
      onError,
    );
  }

  getActivitiesByWorkdayId(
    workdayId: number,
    onSuccess: SuccessCallback<ActivityDTO[]>,
    onError: ErrorCallback,
  ): void {
    worktimeApi.genericJsonPost(
      "user/getActivityByWorkdayId",
      workdayId,
      onSuccess,
      onError,
    );
  }

  updateTeleworkStatus(
    workday: WorkdayDTO,
    onSuccess: SuccessCallback<void>,
    onError: ErrorCallback,
  ): void {
    worktimeApi.genericPut("user/teleworkStatus", workday, onSuccess, onError);
  }

  getFormById(
    formId: number,
    onSuccess: SuccessCallback<FormDTO>,
    onError: ErrorCallback,
  ): void {
    worktimeApi.genericGet(`user/form/${formId}`, onSuccess, onError);
  }

  storeFormWorkdaySubmission(
    formId: number,
    submission: FormSubmissionDTO,
    onSuccess: SuccessCallback<FormSubmissionDTO>,
    onError: ErrorCallback,
  ): void {
    worktimeApi.genericPost(
      `user/storeFormWorkdaySubmission/${formId}`,
      submission,
      onSuccess,
      onError,
    );
  }

  jiraAuthenticate(
    payload: JiraAuthenticateRequestDTO,
    onSuccess: SuccessCallback<void>,
    onError: ErrorCallback,
  ): void {
    worktimeApi.genericPost(
      "user/jira/authenticate",
      payload,
      onSuccess,
      onError,
    );
  }

  jiraImportWorkLogs(
    payload: JiraImportRequestDTO,
    onSuccess: SuccessCallback<WorkdayServiceResponseDTO>,
    onError: ErrorCallback,
  ): void {
    worktimeApi.genericPut(
      "user/jira/import-worklogs",
      payload,
      onSuccess,
      onError,
    );
  }
}

export const checkinApi = new CheckinAPI();
export default checkinApi;
